import { CalendarView } from 'components/domain/calendar/types/CalendarTypes';
import { useOrganization, useQueryParams } from 'hooks';
import useSchedulingSettingsUpdate from 'hooks/models/member-settings/useSchedulingSettingsUpdate';
import useMemberSettings from 'hooks/models/member/useMemberSettings';
import { createContext, useCallback, useContext, useMemo } from 'react';
import { IChildrenProps } from 'types/ChildrenProps';
import { Nullable } from 'types/util/Nullable';
import { FILTER_TYPE, QueryParam } from 'utils/constants/queryParam';
import { SchedulingGridViewListPickerValue } from '../scheduling-grid-view/SchedulingGridViewListPicker/SchedulingGridViewListPicker';
import { ISchedulingPersistence, ScheduledStatus } from '../types/types';

export interface ISchedulingPersistenceContextState {
  setCalendarView: (calendarView: CalendarView) => void;
  setGridViewFilter: (gridViewFilter: SchedulingGridViewListPickerValue) => void;
  setGridViewScheduledStatus: (gridViewScheduledStatus: Nullable<ScheduledStatus>) => void;
  calendarView: CalendarView;
  gridViewFilter: SchedulingGridViewListPickerValue;
  gridViewScheduledStatus: Nullable<ScheduledStatus>;
}

export const SchedulingPersistenceContext = createContext<ISchedulingPersistenceContextState | null>(null);

export function useSchedulingPersistence() {
  const context = useContext(SchedulingPersistenceContext);

  if (context === null || context === undefined) {
    throw Error('useSchedulingPersistence must be nested within a SchedulingPersistenceContextProvider');
  }

  return context;
}

export const defaultSchedulingPersistence: ISchedulingPersistence = {
  calendarView: 'week',
  gridViewFilter: 'Employees',
  gridViewScheduledStatus: null,
};

export function SchedulingPersistenceContextProvider(props: IChildrenProps) {
  const { setParam, getParam } = useQueryParams();
  const organization = useOrganization();
  const memberSettings = useMemberSettings();
  const updateSettings = useSchedulingSettingsUpdate();

  const persistence = memberSettings?.web?.features?.scheduling?.schedulingPersistence ?? defaultSchedulingPersistence;
  const setPersistence = useCallback(
    (newSetting: ISchedulingPersistence) => {
      updateSettings([{ key: 'schedulingPersistence', payload: newSetting }]);
    },
    [updateSettings]
  );

  const setCalendarView = useCallback(
    (calendarView: CalendarView) => {
      const newSettings = {
        ...persistence,
        calendarView,
      };
      setPersistence(newSettings);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setPersistence]
  );

  const setGridViewFilter = useCallback(
    (gridViewFilter: SchedulingGridViewListPickerValue) => {
      const newSettings = {
        ...persistence,
        gridViewFilter,
      };
      setParam(FILTER_TYPE, gridViewFilter);
      setPersistence(newSettings);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setParam, setPersistence]
  );

  const setGridViewScheduledStatus = useCallback(
    (scheduledStatus: Nullable<ScheduledStatus>) => {
      const newSettings = {
        ...persistence,
        gridViewShowOnlyScheduled: scheduledStatus,
      };

      setParam(QueryParam.SCHEDULED_STATUS, scheduledStatus);
      setPersistence(newSettings);
    },
    [persistence, setParam, setPersistence]
  );

  const getFilterType = useCallback(
    (potentialFilterType: string | null): SchedulingGridViewListPickerValue | null => {
      if (
        potentialFilterType === 'Employees' ||
        potentialFilterType === 'Projects' ||
        (potentialFilterType === 'Cost Codes' && organization.trackCostCode) ||
        (potentialFilterType === 'Equipment' && organization.trackEquipment)
      ) {
        return potentialFilterType;
      }

      return null;
    },
    [organization.trackCostCode, organization.trackEquipment]
  );

  const getGridViewFilter = useCallback(() => {
    const param = getFilterType(getParam(FILTER_TYPE));
    if (param) {
      return param;
    } else {
      return persistence.gridViewFilter;
    }
  }, [getParam, getFilterType, persistence]);

  const value = useMemo(
    () => ({
      setCalendarView,
      setGridViewFilter,
      setGridViewScheduledStatus,
      gridViewScheduledStatus: persistence.gridViewScheduledStatus,
      calendarView: persistence.calendarView,
      gridViewFilter: getGridViewFilter(),
    }),
    [
      getGridViewFilter,
      persistence.calendarView,
      persistence.gridViewScheduledStatus,
      setCalendarView,
      setGridViewFilter,
      setGridViewScheduledStatus,
    ]
  );

  return <SchedulingPersistenceContext.Provider value={value}>{props.children}</SchedulingPersistenceContext.Provider>;
}
